import { useId, useState } from 'react'
import * as Mtds from '@mattilsynet/design/react'
import * as mtds from '@mattilsynet/design'
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react/ssr'
import type { Requisition, Sample } from '../../types/types'
import { useAuth } from '../../hooks/useAuth'
import { useSampleCompromisedMutation } from '../../hooks/useRequisition'
import { getFirstRequisition } from '../../utils/requisitionUtils'
import { cancellationErrorMessage } from '../../utils/cancellationErrorMessage'
import { ModalDialog } from '../modal-dialog/ModalDialog'

export const SampleCancellationForm = ({
  sample,
  onSuccess,
  requisition: suppliedRequisition,
  missingResultsOnly = false,
  onPendingChange
}: {
  sample: Sample
  onSuccess: () => void
  requisition?: Requisition
  missingResultsOnly?: boolean
  onPendingChange?: (pending: boolean) => void
}) => {
  const { userId, isAdmin } = useAuth()
  const requisition = suppliedRequisition ?? getFirstRequisition(sample)
  const mutation = useSampleCompromisedMutation()
  const subSamples = [...(sample.subSamples ?? [])].sort(
    (a, b) => a.subSampleId - b.subSampleId
  )
  const hasResults = (id: number) =>
    (requisition?.results?.subSamples?.[id]?.resultSets?.length ?? 0) > 0
  const eligible = subSamples.filter(
    (item) => !item.compromised && !hasResults(item.subSampleId)
  )
  const cancelled = subSamples.filter((item) => item.compromised)
  const [undoIds, setUndoIds] = useState<number[]>([])
  const [undoOpen, setUndoOpen] = useState(false)
  const undoDialogId = useId()
  const wholeSampleCancelled = sample.state === 'Compromised'
  const [cancelIds, setCancelIds] = useState<number[]>([])
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string>()
  const [reasonError, setReasonError] = useState<string>()
  const [undoError, setUndoError] = useState<string>()
  const authorized = isAdmin === true || sample.userId === `user:${userId}`
  const finalized = sample.state === 'Completed' || sample.state === 'Closed'
  if (!authorized || finalized || !requisition) {
    return (
      <Mtds.Alert data-color='info'>
        {!authorized
          ? 'Du har ikke tilgang til å endre kanselleringen.'
          : finalized
            ? 'Prøven er ferdigstilt og må gjenåpnes før kansellering kan endres.'
            : 'Prøven mangler rekvisisjon.'}
      </Mtds.Alert>
    )
  }
  const toggle = (ids: number[], id: number) =>
    ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]
  const submit = async () => {
    if (mutation.isPending) return
    setError(undefined)
    if (!reason.trim()) {
      setReasonError('Oppgi en grunn til kansellering.')
      return
    }
    // Revalidate against current server data, not just the initially selected checkboxes.
    const selected = cancelIds.filter((id) =>
      eligible.some((item) => item.subSampleId === id)
    )
    if (!selected.length) {
      setError('Velg minst én delprøve.')
      return
    }
    try {
      onPendingChange?.(true)
      await mutation.mutateAsync({
        requisitionId: requisition.id,
        body: {
          subSamples: selected.map((id) => ({
            subsampleid: id,
            state: true,
            reason: reason.trim()
          }))
        }
      })
      mtds.toast.success('Valgte delprøver er kansellert')
      onSuccess()
    } catch (failure) {
      setError(cancellationErrorMessage(failure, false))
    } finally {
      onPendingChange?.(false)
    }
  }
  const undoCancellation = async () => {
    if (mutation.isPending) return
    const selected = undoIds.filter((id) =>
      cancelled.some((item) => item.subSampleId === id)
    )
    if (!selected.length) return
    setUndoError(undefined)
    try {
      onPendingChange?.(true)
      await mutation.mutateAsync({
        requisitionId: requisition.id,
        body: {
          subSamples: selected.map((id) => ({
            subsampleid: id,
            state: false,
            reason: ''
          }))
        }
      })
      setUndoIds([])
      setUndoOpen(false)
      mtds.toast.success('Kanselleringen av valgte delprøver er angret')
    } catch (failure) {
      setUndoError(cancellationErrorMessage(failure, true))
    } finally {
      onPendingChange?.(false)
    }
  }
  return (
    <Mtds.Grid data-gap='6'>
      {error && <Mtds.Alert data-color='danger'>{error}</Mtds.Alert>}
      <Mtds.Grid
        as='form'
        aria-label='Kanseller delprøver'
        data-gap='4'
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        {wholeSampleCancelled ? (
          <Mtds.Alert data-color='info'>
            Hele prøven er kansellert. Du kan angre kanselleringen for
            delprøvene nedenfor.
          </Mtds.Alert>
        ) : (
          <p>
            {missingResultsOnly ? (
              'Disse delprøvene mangler analyseresultater. Velg dem som ikke skal analyseres, og oppgi en grunn til kansellering.'
            ) : (
              <>
                Velg delprøvene du vil kansellere. Hele prøven blir kansellert
                når alle delprøvene er kansellert.
              </>
            )}
          </p>
        )}
        {!wholeSampleCancelled && eligible.length === 0 && (
          <Mtds.Alert data-color='info'>
            Ingen delprøver kan kanselleres.
          </Mtds.Alert>
        )}
        <Mtds.Grid data-gap='3'>
          {subSamples
            .filter(
              (item) =>
                !item.compromised &&
                (!missingResultsOnly || !hasResults(item.subSampleId))
            )
            .map((item) => (
              <Mtds.Field
                key={item.subSampleId}
                as='input'
                type='checkbox'
                label={`Delprøve ${item.subSampleId}`}
                description={
                  hasResults(item.subSampleId)
                    ? 'Har mottatt analyseresultater og kan ikke kanselleres.'
                    : undefined
                }
                checked={
                  !hasResults(item.subSampleId) &&
                  cancelIds.includes(item.subSampleId)
                }
                disabled={mutation.isPending || hasResults(item.subSampleId)}
                onChange={() =>
                  setCancelIds(toggle(cancelIds, item.subSampleId))
                }
              />
            ))}
        </Mtds.Grid>
        {eligible.length > 0 && (
          <Mtds.Field
            as='textarea'
            label='Grunn til kansellering'
            required
            rows={3}
            disabled={mutation.isPending || eligible.length === 0}
            value={reason}
            validation={reasonError}
            onChange={(event) => {
              setReason(event.target.value)
              setReasonError(undefined)
            }}
          />
        )}
        {eligible.length > 0 && (
          <Mtds.Flex>
            <Mtds.Button
              type='submit'
              data-variant='primary'
              data-color='danger'
              disabled={
                mutation.isPending ||
                !eligible.some((item) => cancelIds.includes(item.subSampleId))
              }
            >
              Kanseller valgte
            </Mtds.Button>
          </Mtds.Flex>
        )}
      </Mtds.Grid>
      {!missingResultsOnly && cancelled.length > 0 && (
        <Mtds.Grid as='section' aria-label='Kansellerte delprøver' data-gap='4'>
          <Mtds.Divider />
          <Mtds.Grid data-gap='3'>
            <Mtds.Heading as='h3' data-size='xs'>
              Kansellerte delprøver
            </Mtds.Heading>
            <Mtds.Grid as='ul' data-gap='2'>
              {cancelled.map((item) => (
                <li key={item.subSampleId}>
                  <Mtds.Field
                    as='input'
                    type='checkbox'
                    label={`Delprøve ${item.subSampleId}`}
                    checked={undoIds.includes(item.subSampleId)}
                    disabled={mutation.isPending}
                    onChange={() =>
                      setUndoIds(toggle(undoIds, item.subSampleId))
                    }
                  />
                </li>
              ))}
            </Mtds.Grid>
          </Mtds.Grid>
          <Mtds.Flex>
            <Mtds.Button
              type='button'
              data-variant='secondary'
              disabled={
                mutation.isPending ||
                !cancelled.some((item) => undoIds.includes(item.subSampleId))
              }
              onClick={() => {
                setUndoError(undefined)
                setUndoOpen(true)
              }}
            >
              <ArrowCounterClockwiseIcon /> Angre kansellering av valgte
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Grid>
      )}
      <ModalDialog
        id={undoDialogId}
        open={undoOpen}
        aria-labelledby={`${undoDialogId}-title`}
        closedby={mutation.isPending ? 'none' : 'any'}
        onClose={(event) => {
          event.stopPropagation()
          if (!mutation.isPending) setUndoOpen(false)
        }}
      >
        <Mtds.Button
          type='button'
          command='close'
          commandfor={undoDialogId}
          aria-label='Lukk'
          disabled={mutation.isPending}
        />
        <Mtds.Grid data-gap='4'>
          <Mtds.Heading as='h2' data-size='sm' id={`${undoDialogId}-title`}>
            Angre kansellering
          </Mtds.Heading>
          <p>
            Kanselleringen av valgte delprøver i prøve {sample.id} blir angret.
            Delprøvene blir aktive igjen.
          </p>
          {undoError && (
            <Mtds.Alert data-color='danger'>{undoError}</Mtds.Alert>
          )}
          <Mtds.Flex data-justify='end'>
            <Mtds.Button
              type='button'
              data-variant='secondary'
              disabled={mutation.isPending}
              onClick={() => setUndoOpen(false)}
            >
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              type='button'
              data-variant='primary'
              disabled={
                mutation.isPending ||
                !cancelled.some((item) => undoIds.includes(item.subSampleId))
              }
              aria-busy={mutation.isPending}
              onClick={() => void undoCancellation()}
            >
              Bekreft
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Grid>
      </ModalDialog>
    </Mtds.Grid>
  )
}
